import { FC } from 'react'
import { ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'
import type { PmsJobCombinedDto } from '../../../core/pms/_models'
import { useAuth } from '../../../../auth'

interface Props {
  jobs: PmsJobCombinedDto[]
  loading: boolean
  totalCount?: number
  onViewAll: () => void
}

const meta: CardMeta = {
  description: 'Jobs past their due date requiring immediate attention. Shows vessel, equipment, job title, and priority.',
  endpoint: 'GET /pms/jobs/combined?state=OVERDUE&size=5',
  logic: 'Fetches top 5 overdue jobs sorted by dueDate ascending. Priority badge uses criticality field.',
}

const priorityBadge = (c?: string | null) => {
  const map: Record<string, string> = {
    CRITICAL: 'bg-red-100 text-red-600',
    HIGH: 'bg-red-100 text-red-600',
    MEDIUM: 'bg-amber-100 text-amber-600',
    LOW: 'bg-slate-100 text-slate-600',
  }
  return map[c || ''] || 'bg-slate-100 text-slate-600'
}

const OverdueJobsTable: FC<Props> = ({ jobs, loading, totalCount, onViewAll }) => {
  const navigate = useNavigate()
  const {currentUser, auth} = useAuth()
  const roleId = Number((auth?.userDetails as any)?.roleId ?? currentUser?.role?.id ?? 0) || 0
  const isCrew = roleId === 4

  const total = totalCount ?? jobs.length
  const displayedCount = Math.min(jobs.length, 5)

  return (
    <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
      <div className='flex items-center justify-between mb-3'>
        <div>
          <div className='flex items-center gap-2'>
            <h3 className='text-sm font-bold text-slate-900'>Top Overdue Jobs</h3>
            {!loading && total > 0 && (
              <span className='px-2 py-0.5 text-[10px] font-medium bg-red-50 text-red-600 rounded-full border border-red-100'>
                {total > displayedCount ? `First ${displayedCount} of ${total}` : `${total} total`}
              </span>
            )}
          </div>
          {!loading && total > 5 && (
            <p className='text-[10px] text-slate-400 mt-0.5'>Showing first 5 overdue jobs. Click 'View all' for more.</p>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <CardInfoButton meta={meta} />
          <button onClick={onViewAll} className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'>
            View all <ArrowRight className='w-3 h-3' />
          </button>
        </div>
      </div>
      <div className='overflow-x-auto flex-1'>
        <table className='w-full text-left text-[10px] min-w-[280px]'>
          <thead>
            <tr className='text-slate-400 uppercase'>
              {!isCrew && <th className='pb-2 font-medium'>Vessel</th>}
              <th className='pb-2 font-medium'>Equipment</th>
              <th className='pb-2 font-medium'>Job</th>
              <th className='pb-2 font-medium'>Priority</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className='border-t border-slate-50 animate-pulse'>
                    {!isCrew && <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>}
                    <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-20'></div></td>
                    <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-28'></div></td>
                    <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                  </tr>
                ))}
              </>
            )}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={isCrew ? 3 : 4} className='py-8 text-center text-slate-400'>No overdue jobs</td></tr>
            )}
            {!loading && jobs.slice(0, 5).map((j) => (
              <tr
                key={j.job.id}
                onClick={() => navigate('/technical/pms/jobs?state=OVERDUE')}
                className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
              >
                {!isCrew && <td className='py-2 font-medium text-slate-800'>{j.job.vesselName || '—'}</td>}
                <td className='py-2 text-slate-600'>{j.job.equipmentName || '—'}</td>
                <td className='py-2 text-slate-700'>{j.job.title || j.job.jobCode || '—'}</td>
                <td className='py-2'>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${priorityBadge(j.job.criticality)}`}>
                    {j.job.criticality || 'N/A'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default OverdueJobsTable
